import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './payments.css';

const Payments = () => {
  const [mode, setMode] = useState(''); // retailer or supplier
  const [retailers, setRetailers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nameInput, setNameInput] = useState('');
  const [filteredList, setFilteredList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [receiptNo, setReceiptNo] = useState('');
  const [message, setMessage] = useState('');

  // Fetch retailers and suppliers
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rRes, sRes] = await Promise.all([
          axios.get('/retailers'),
          axios.get('/suppliers'),
        ]);
        // Sort by name immediately when fetching
        setRetailers(rRes.data.sort((a, b) => a.name.localeCompare(b.name)));
        setSuppliers(sRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Error loading entities:', err);
      }
    };

    fetchData();
  }, []);

  // Filter based on mode and input
  useEffect(() => {
    if (!nameInput) {
      setFilteredList([]);
      return;
    }

    let list = [];
    if (mode === 'retailer') {
      list = retailers.filter(r =>
        r.name.toLowerCase().includes(nameInput.toLowerCase())
      );
    } else if (mode === 'supplier') {
      list = suppliers.filter(s =>
        s.name.toLowerCase().includes(nameInput.toLowerCase())
      );
    }

    setFilteredList(list);
    setShowDropdown(list.length > 0);
  }, [nameInput, mode, retailers, suppliers]);

  const handleSelectEntity = (entity) => {
    setNameInput(entity.name);
    setSelectedId(entity[`${mode}_id`]);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId || !mode || !amount || !paymentType || !paymentDate) {
      return setMessage('Please fill all required fields.');
    }

    const payload = {
      amount: parseFloat(amount),
      payment_type: paymentType,
      receipt_no: receiptNo || null,
      payment_date: paymentDate,
    };

    try {
      await axios.post(`/${mode}s/${selectedId}/payments`, payload);
      setMessage('✅ Payment recorded successfully.');
      setAmount('');
      setReceiptNo('');
    } catch (err) {
      console.error('Error posting payment:', err);
      setMessage('❌ Failed to record payment.');
    }
  };

  return (
    <div className="payments-page">
      <h2>Record Payment</h2>

      <form onSubmit={handleSubmit} className="payment-form">
        {/* Step 1: Select mode with buttons */}
        <div className="form-group">
          <label>Payment Type:</label>
          <div className="mode-selector">
            <button
              type="button"
              className={`mode-btn ${mode === 'retailer' ? 'active' : ''}`}
              onClick={() => {
                setMode('retailer');
                setNameInput('');
                setSelectedId('');
                setFilteredList([]);
              }}
            >
              Retailer Payment
            </button>
            <button
              type="button"
              className={`mode-btn ${mode === 'supplier' ? 'active' : ''}`}
              onClick={() => {
                setMode('supplier');
                setNameInput('');
                setSelectedId('');
                setFilteredList([]);
              }}
            >
              Supplier Payment
            </button>
          </div>
        </div>

        {/* Step 2: Search Name */}
        {mode && (
          <div className="form-group search-container">
            <label>Search {mode}:</label>
            <div className="search-input-wrapper">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value);
                  setSelectedId('');
                }}
                onFocus={() => nameInput && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                placeholder={`Type ${mode} name...`}
                required
              />
              {showDropdown && (
                <div className="search-dropdown">
                  {filteredList.length > 0 ? (
                    filteredList.map((entity) => (
                      <div
                        key={entity[`${mode}_id`]}
                        className="dropdown-item"
                        onClick={() => handleSelectEntity(entity)}
                      >
                        {entity.name}
                        {entity.contact_number && (
                          <span className="contact-info">{entity.contact_number}</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item no-results">No results found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Fields */}
        <div className="form-group">
          <label>Amount (₹):</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            onWheel={(e) => e.target.blur()}
          />
        </div>

        <div className="form-group">
          <label>Payment Method:</label>
          <select 
            value={paymentType} 
            onChange={(e) => setPaymentType(e.target.value)}
            required
          >
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="bank_transfer">Bank Transfer</option>
          </select>
        </div>

        <div className="form-group">
          <label>Payment Date:</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Receipt No (optional):</label>
          <input
            type="text"
            value={receiptNo}
            onChange={(e) => setReceiptNo(e.target.value)}
            placeholder="Enter receipt number"
          />
        </div>

        <button type="submit" className="submit-btn">
          Record Payment
        </button>
      </form>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default Payments;